import type React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import { Stack } from '@mui/material';
import { TYPES } from '@/engine/ioc';
import { useInjection } from 'inversify-react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { observer } from 'mobx-react';
import type { PointsSwitcherController } from './PointsSwitcher.controller';

export const PointsSwitcher: React.FC = observer(() => {
  const controller = useInjection<PointsSwitcherController>(TYPES.PointsSwitcherController);

  useEffect(() => {
    document.addEventListener('keydown', _handleKeyDown, true);
    return (): void => {
      document.removeEventListener('keydown', _handleKeyDown, true);
    };
  });

  const splineViewModel = controller.selectedSplineViewModel;

  const _handleKeyDown = (event: KeyboardEvent): void => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (splineViewModel === undefined) return;
    switch (event.key) {
      case 'ArrowRight':
        controller.nextPoint(splineViewModel);
        break;
      case 'ArrowLeft':
        controller.prevPoint(splineViewModel);
        break;
      default:
        break;
    }
  };

  if (!splineViewModel?.isSelected) {
    return null;
  }

  return (
    <Container>
      <Stack direction="row" spacing={2} justifyContent="center" sx={{ pointerEvents: 'none' }}>
        <Button
          data-testid="backBtn"
          variant="text"
          size="small"
          sx={{ pointerEvents: 'auto' }}
          onClick={(): void => controller.prevPoint(splineViewModel)}
          startIcon={<ArrowBackIcon />}
        >
          PREVIOUS POINT
        </Button>
        <Button
          variant="text"
          size="small"
          sx={{ pointerEvents: 'auto' }}
          onClick={(): void => controller.nextPoint(splineViewModel)}
          endIcon={<ArrowForwardIcon />}
        >
          NEXT POINT
        </Button>
      </Stack>
    </Container>
  );
});

const Container = styled.div`
  position: absolute;
  bottom: 1em;
  width: 100%;
  z-index: 996;
  pointer-events: none;
`;
